import apiInstance from "api/axios";
import { Requests } from "config/api";
import {
  CarCaptureDraftType,
  CaptureDraftImageType,
  DealerType,
} from "types/loadedData";

const replacePath = (route: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (current, [key, value]) => current.replace(`:${key}`, value),
    route
  );

export type CaptureDraftPatch = {
  price?: string | number;
  make?: string;
  model?: string;
  color?: string;
  year?: string | number;
  mileage?: string | number;
  batteryRangeKm?: string | number;
  transmissionType?: string;
  fuelType?: string;
  usageType?: string;
  listingType?: string;
  location?: string;
  description?: string;
  imageOrder?: string[];
  selectedImageIds?: string[];
};

export const getCaptureDrafts = async (params?: {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ drafts: CarCaptureDraftType[]; count: number }> => {
  const response = await apiInstance.get(Requests.get.getCaptureDrafts, { params });
  return {
    drafts: response.data?.drafts || [],
    count: response.data?.count || 0,
  };
};

export const getCaptureDraft = async (
  id: string
): Promise<CarCaptureDraftType> => {
  const response = await apiInstance.get(
    replacePath(Requests.get.getCaptureDraft, { id })
  );
  return response.data?.draft;
};

export const createCaptureDraft = async (payload: {
  dealer: string;
  clientDraftId: string;
  price?: string | number;
  usageType?: "used" | "new";
  color?: string;
  description?: string;
}): Promise<CarCaptureDraftType> => {
  const response = await apiInstance.post(Requests.post.createCaptureDraft, payload);
  return response.data?.draft;
};

export const updateCaptureDraft = async (
  id: string,
  payload: CaptureDraftPatch
): Promise<CarCaptureDraftType> => {
  const response = await apiInstance.patch(
    replacePath(Requests.update.updateCaptureDraft, { id }),
    payload
  );
  return response.data?.draft;
};

export const uploadCaptureDraftImage = async (
  id: string,
  file: Blob,
  clientId: string,
  fileName = "capture.jpg"
): Promise<CaptureDraftImageType> => {
  const formData = new FormData();
  formData.append("image", file, fileName);
  formData.append("clientId", clientId);
  const response = await apiInstance.post(
    replacePath(Requests.post.uploadCaptureDraftImage, { id }),
    formData
  );
  return response.data?.image;
};

export const replaceCaptureDraftImage = async (
  draftId: string,
  imageId: string,
  file: Blob
): Promise<CaptureDraftImageType> => {
  const formData = new FormData();
  formData.append("image", file, "cropped.jpg");
  const response = await apiInstance.patch(
    replacePath(Requests.update.replaceCaptureDraftImage, {
      id: draftId,
      imageId,
    }),
    formData
  );
  return response.data?.image;
};

export const deleteCaptureDraftImage = async (
  draftId: string,
  imageId: string
) => {
  await apiInstance.delete(
    replacePath(Requests.delete.deleteCaptureDraftImage, {
      id: draftId,
      imageId,
    })
  );
};

export const publishCaptureDraft = async (
  id: string
): Promise<CarCaptureDraftType> => {
  const response = await apiInstance.post(
    replacePath(Requests.post.publishCaptureDraft, { id })
  );
  return response.data?.draft;
};

export const retryCaptureTelegram = async (id: string) => {
  const response = await apiInstance.post(
    replacePath(Requests.post.retryCaptureTelegram, { id })
  );
  return response.data?.draft as CarCaptureDraftType;
};

export const deleteCaptureDraft = async (id: string) => {
  await apiInstance.delete(replacePath(Requests.delete.deleteCaptureDraft, { id }));
};

export const quickCreateDealer = async (payload: {
  phoneNumber: string;
  name?: string;
}): Promise<DealerType> => {
  const response = await apiInstance.post(Requests.post.quickCreateDealer, payload);
  return response.data?.dealer;
};
